import { createElement } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { EventBus } from '../core/EventBus';
import type { Store } from '../core/Store';
import type { ResolvedConfig, PlatformCapabilities } from '../core/types';
import type { UIRegistry } from '../core/UIRegistry';
import type { IEngine } from '../engine/IEngine';
import { ReactPlayerShell } from './ReactPlayerShell';
import type { IPlayerShell } from './IPlayerShell';

export class ReactShellAdapter implements IPlayerShell {
  private reactRoot: Root;
  private wrapper: HTMLDivElement;
  private rootEl: HTMLDivElement | null = null;
  private resolveRoot!: (el: HTMLDivElement) => void;
  private rootPromise: Promise<HTMLDivElement>;

  constructor(
    container: HTMLElement,
    eventBus: EventBus,
    store: Store,
    config: ResolvedConfig,
    getEngine: () => IEngine | null,
    uiRegistry: UIRegistry,
    capabilities?: PlatformCapabilities,
  ) {
    this.rootPromise = new Promise<HTMLDivElement>((resolve) => {
      this.resolveRoot = resolve;
    });

    this.wrapper = document.createElement('div');
    this.wrapper.style.width = '100%';
    this.wrapper.style.height = '100%';
    container.appendChild(this.wrapper);

    this.reactRoot = createRoot(this.wrapper);

    const rootRef = (el: HTMLDivElement | null) => {
      if (el) {
        this.rootEl = el;
        this.resolveRoot(el);
      }
    };

    this.reactRoot.render(
      createElement(ReactPlayerShell, {
        rootRef,
        eventBus,
        store,
        config,
        getEngine,
        uiRegistry,
        capabilities,
      }),
    );
  }

  getRoot(): HTMLElement {
    // Return immediately if available; engine.attach is called after await in GGPlayer.init
    return this.rootEl!;
  }

  /** Wait for the React root to mount (used by GGPlayer.init) */
  async waitForRoot(): Promise<HTMLDivElement> {
    return this.rootPromise;
  }

  destroy(): void {
    this.reactRoot.unmount();
    this.wrapper.remove();
  }
}
